import AddBoxIcon from '@mui/icons-material/AddBox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import { MenuItem, Menu, Fade, Grid, Stack, List, ListItemButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import React, { FC, useState } from 'react';
import { ISubmissionFormSettings } from 'wisestep-api';

import { getSocialIconByURL } from '../../helper/SocialIcons/SocialIcons';
import WsColors from '../../WsColors/index';
import EmailList from '../EmailList/EmailList';
import Item from '../Item/Item';
import Label from '../Label/Label';
import PhoneNumberList from '../PhoneNumberList/PhoneNumberList';
import TagsList from '../TagsList/TagsList';
import { WsElementWithPopOver } from '../WsElementWithPopOver';
import { WsIconText } from '../WsIconText';
import WsTypography from '../WsTypography/WsTypography';

const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '7px',
}));

const Img = styled('img')(() => ({
  height: '22px',
  width: '22px',
  cursor: 'pointer',
  marginRight: '7px',
}));

interface ICandidateCommonContactInfo {
  ss: 'small' | 'large';
  email: string[];
  phoneNumbers: string[];
  tags: string[];
  profileUrls: string[];
  name?: string;
  alternateEmails?: string[];
  handleProfileAction: (arg1: string, arg2: string | null) => void;
  personalWebsite?: string;
  settings?: ISubmissionFormSettings | null;
}

const CandidateCommonContactInfo: FC<ICandidateCommonContactInfo> = ({
  email,
  phoneNumbers,
  tags,
  profileUrls,
  ss = 'small',
  name = '',
  alternateEmails = [],
  handleProfileAction,
  personalWebsite,
  settings,
}) => {
  const openUrlInNewTab = (url: string) => {
    const newWindow =
      url.indexOf('http') > -1 || url.indexOf('https') > -1
        ? window.open(url, '_blank', 'noopener,noreferrer')
        : window.open('//' + url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const ProfileUrlMenuData: FC<{ url: string; index: number }> = ({ url, index }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClose = () => setAnchorEl(null);
    return (
      <>
        <Img
          src={getSocialIconByURL(url)}
          key={index}
          alt={url}
          onClick={() => openUrlInNewTab(url)}
        />
        <Menu
          id="fade-menu"
          MenuListProps={{
            'aria-labelledby': 'fade-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
        >
          <MenuItem onClick={() => openUrlInNewTab(url)}>Open</MenuItem>
          <MenuItem onClick={() => handleProfileAction('copy', url)}>Copy</MenuItem>
          {/* {index !== 0 && (
            <MenuItem onClick={() => handleProfileAction('deleteLink', url)}>Delete</MenuItem>
          )} */}
        </Menu>
      </>
    );
  };

  return (
    <>
      <Grid mt={2} item xs={ss == 'small' ? 2 : 4}>
        <GridItem>
          <Label>Name</Label>
        </GridItem>
      </Grid>
      <Grid mt={2} item xs={ss == 'small' ? 10 : 8}>
        <GridItem>
          <Stack direction="row" spacing={2} mt={1}>
            <WsTypography bold={true}>{name}</WsTypography>
            <EditIcon
              sx={{ fontSize: '21px', color: WsColors.TheBlues500 }}
              onClick={() => handleProfileAction('updateName', name)}
            />
          </Stack>
        </GridItem>
      </Grid>
      <Grid item xs={ss == 'small' ? 2 : 4} mt={2}>
        <GridItem>
          {ss == 'small' ? (
            <EmailOutlinedIcon sx={{ color: WsColors.TheBlues500 }} />
          ) : (
            <Label>Email</Label>
          )}
        </GridItem>
      </Grid>
      <Grid item xs={ss == 'small' ? 10 : 8} mt={2}>
        <GridItem>
          <EmailList emails={email} handleProfileAction={handleProfileAction} main={true} />
        </GridItem>
      </Grid>
      {settings && settings?.alternateEmails && settings?.alternateEmails.hidden === false && (
        <>
          {!isEmpty(alternateEmails) && (
            <>
              <Grid item xs={ss == 'small' ? 2 : 4} mt={2}>
                <GridItem>
                  {ss == 'small' ? (
                    <EmailOutlinedIcon sx={{ color: WsColors.TheBlues500 }} />
                  ) : (
                    <Label>Alternate Emails</Label>
                  )}
                </GridItem>
              </Grid>
              <Grid item xs={ss == 'small' ? 10 : 8} mt={2}>
                <GridItem>
                  <EmailList emails={alternateEmails} handleProfileAction={handleProfileAction} />
                </GridItem>
              </Grid>
            </>
          )}
        </>
      )}

      {settings && settings?.phoneNumber && settings?.phoneNumber.hidden === false && (
        <>
          <Grid item xs={ss == 'small' ? 2 : 4} mt={2}>
            <GridItem>
              {ss == 'small' ? (
                <LocalPhoneOutlinedIcon sx={{ color: WsColors.TheBlues500 }} />
              ) : (
                <Label>Phone no.</Label>
              )}
            </GridItem>
          </Grid>
          <Grid item xs={ss == 'small' ? 10 : 8} mt={2}>
            <GridItem>
              <PhoneNumberList
                phoneNumbers={phoneNumbers}
                handleProfileAction={handleProfileAction}
              />
            </GridItem>
          </Grid>
        </>
      )}

      {settings && settings?.socialLinks && settings?.socialLinks.hidden === false && (
        <>
          <Grid item xs={ss == 'small' ? 2 : 4} mt={2}>
            <GridItem>
              {ss == 'small' ? (
                <InsertLinkIcon sx={{ color: WsColors.TheBlues500, transform: 'rotate(140deg)' }} />
              ) : (
                <Label>Social Media</Label>
              )}
            </GridItem>
          </Grid>
          <Grid item xs={ss == 'small' ? 10 : 8} mt={2}>
            <GridItem>
              <Stack direction="row" spacing={2} mt={1}>
                {profileUrls.length > 0 ? (
                  profileUrls.map((url: string, index: number) => (
                    <WsElementWithPopOver
                      key={index}
                      element={<ProfileUrlMenuData url={url} index={index} />}
                    >
                      <List dense={true} sx={{ pl: 1 }}>
                        <ListItemButton>
                          <WsIconText
                            text="Copy"
                            icon={
                              <ContentCopyIcon
                                key={0}
                                fontSize="small"
                                sx={{ color: WsColors.TheBlues500 }}
                              />
                            }
                            onClick={() => handleProfileAction('copy', url)}
                            textCursor="pointer"
                          />
                        </ListItemButton>
                      </List>
                    </WsElementWithPopOver>
                  ))
                ) : (
                  <Stack direction="row" spacing={3} mt={1}>
                    <WsTypography bold={true}>Not added</WsTypography>
                    <AddBoxIcon
                      sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                      onClick={() => handleProfileAction('addLink', null)}
                    />
                  </Stack>
                )}
                {profileUrls.length > 0 && (
                  <AddBoxIcon
                    sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                    onClick={() => handleProfileAction('addLink', null)}
                  />
                )}
              </Stack>
            </GridItem>
          </Grid>
        </>
      )}

      {settings && settings?.personalWebsite && settings?.personalWebsite.hidden === false && (
        <>
          <Grid item xs={ss == 'small' ? 2 : 4} mt={2}>
            <GridItem>
              {ss == 'small' ? (
                <WebAssetIcon sx={{ color: WsColors.TheBlues500 }} />
              ) : (
                <Label>Website</Label>
              )}
            </GridItem>
          </Grid>
          {personalWebsite ? (
            <Grid item xs={ss == 'small' ? 10 : 8} mt={2}>
              <GridItem>
                <WsTypography bold={true}>{personalWebsite}</WsTypography>
              </GridItem>
            </Grid>
          ) : (
            <Grid item xs={ss == 'small' ? 10 : 8} mt={2}>
              <GridItem>
                <WsTypography bold={true}>{'Not added'}</WsTypography>
              </GridItem>
            </Grid>
          )}
        </>
      )}
      {settings && settings?.tags && settings?.tags.hidden === false && (
        <>
          <Grid item xs={ss == 'small' ? 2 : 4} mt={2}>
            <GridItem>
              {ss == 'small' ? (
                <SellOutlinedIcon sx={{ color: WsColors.TheBlues500 }} />
              ) : (
                <Label>Tags</Label>
              )}
            </GridItem>
          </Grid>
          <Grid item xs={ss == 'small' ? 10 : 8} mt={2}>
            <GridItem>
              <TagsList tags={tags} handleProfileAction={handleProfileAction} />
            </GridItem>
          </Grid>
        </>
      )}
    </>
  );
};

export default CandidateCommonContactInfo;
